import { useState, useEffect } from 'react';
import { Swiper } from 'swiper/types';

interface UseCarouselProps {
  slides?: Array<any> | null;
  initialSlidesPerView?: number;
  initialSlidesPerGroup?: number;
}

export function useCarousel({
    slides,
    initialSlidesPerView = 3.5,
    initialSlidesPerGroup = 2
}: UseCarouselProps) {
    const [swiperComponent, setSwiperComponent] = useState<Swiper | null>(null);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [noSlides, setNoSlides] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Handle mobile detection
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Initialize arrow visibility and slides presence
    useEffect(() => {
    // Initialize right arrow visibility
        if (slides && slides.length > 1) {
            setShowRightArrow(true);
        } else {
            setShowRightArrow(false);
        }

        // Check if there are slides
        slides && slides.length > 0
            ? setNoSlides(false)
            : setNoSlides(true);
    }, [slides]);

    // Navigation functions
    const slideNext = () => {
        swiperComponent?.slideNext();
    };

    const slidePrev = () => {
        swiperComponent?.slidePrev();
    };

    // Slide change handler
    const onSlideChange = () => {
        const activeIndex = swiperComponent?.activeIndex ?? 0;

        // Handle left arrow visibility
        if (activeIndex > 0) {
            setShowLeftArrow(true);
        } else {
            setShowLeftArrow(false);
        }

        // Handle right arrow visibility
        if (swiperComponent?.isEnd) {
            setShowRightArrow(false);
        } else {
            setShowRightArrow(true);
        }
    };

    // From edge handler (when returning from end)
    const onFromEdge = () => {
        if (!swiperComponent?.isEnd) {
            setShowRightArrow(true);
        }
    };

    // Calculate effective slides per view based on props or mobile status
    const effectiveSlidesPerView = isMobile
        ? 1
        : initialSlidesPerView;

    const effectiveSlidesPerGroup = isMobile
        ? 1
        : initialSlidesPerGroup;

    return {
    // State
        swiperComponent,
        showRightArrow,
        showLeftArrow,
        noSlides,
        isMobile,

        // Setters
        setSwiperComponent,

        // Navigation
        slideNext,
        slidePrev,

        // Event handlers
        onSlideChange,
        onFromEdge,

        // Calculated values
        effectiveSlidesPerView,
        effectiveSlidesPerGroup
    };
}
